import React, { useMemo } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Paper,
  Group,
  ThemeIcon,
  Badge,
  useMantineColorScheme,
  Loader,
  Center,
  Alert,
  Avatar,
  Button,
} from "@mantine/core";
import {
  IconUserPlus,
  IconClipboardList,
  IconHistory,
  IconAlertCircle,
  IconBuilding,
  IconArrowLeft,
} from "@tabler/icons-react";
import {
  useMeQuery,
  useAllActiveFranchisesWithStaffQuery,
  usePendingFranchiseApprovalsQuery,
} from "generated/graphql";
import FranchisePendingApprovals from "./FranchisePendingApprovals";
import TransactionCreate from "./TransactionCreate";

/**
 * Franchise data with user context
 */
interface ManagedFranchise {
  id: string;
  name: string;
  prefix: string | null;
  logoUrl: string | null;
  role: "GM" | "AGM";
}

/**
 * Hook to get the franchise managed by the current user.
 * A user can only be GM of one franchise at a time.
 */
function useUserFranchise() {
  const { data: meData, loading: meLoading, error: meError } = useMeQuery();
  const {
    data: franchisesData,
    loading: franchisesLoading,
    error: franchisesError,
  } = useAllActiveFranchisesWithStaffQuery();

  const userId = meData?.me?.userData?.id;

  // Find the single franchise where user is GM or AGM
  const franchise = useMemo<ManagedFranchise | null>(() => {
    const items = franchisesData?.franchises?.items;
    if (!userId || !items || !Array.isArray(items)) return null;

    const found = items.find(
      (f) => f.GM?.id === userId || f.AGM?.id === userId
    );

    if (!found) return null;

    return {
      id: found.id,
      name: found.name,
      prefix: found.prefix ?? null,
      logoUrl: found.logoUrl ?? null,
      role: found.GM?.id === userId ? ("GM" as const) : ("AGM" as const),
    };
  }, [userId, franchisesData]);

  return {
    userId,
    franchise,
    loading: meLoading || franchisesLoading,
    error: meError || franchisesError,
  };
}

/**
 * GM Hub Dashboard
 *
 * Main dashboard showing the user's managed franchise and quick actions.
 */
function GMHubDashboard() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const { franchise, loading, error } = useUserFranchise();

  // Get pending count for the franchise (for badge)
  const { data: pendingData } = usePendingFranchiseApprovalsQuery({
    variables: { franchiseId: franchise?.id ?? "" },
    skip: !franchise?.id,
  });
  const pendingCount = pendingData?.pendingFranchiseApprovals?.totalCount ?? 0;

  if (loading) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load your franchise information. {error.message}
        </Alert>
      </Container>
    );
  }

  if (!franchise) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Alert icon={<IconAlertCircle size={16} />} title="No Franchise Assigned" color="yellow">
          You are not currently assigned as GM or AGM of any franchise.
          Contact league administration if you believe this is an error.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
      <div className="space-y-6">
        <div>
          <Title c={isDark ? "white" : "black"} size="h2" className="sm:text-3xl">
            General Manager Hub
          </Title>
          <Text c={isDark ? "gray.4" : "gray.7"} size="sm" mt="xs">
            Manage your franchise, submit transactions, and review pending approvals.
          </Text>
        </div>

        {/* Your Franchise */}
        <div>
          <Title order={4} c={isDark ? "gray.3" : "gray.8"} mb="sm">
            Your Franchise
          </Title>
          <Paper
            withBorder
            radius="md"
            p="md"
            shadow="sm"
            className="flex items-center gap-4"
            style={{ maxWidth: 400 }}
          >
            <Avatar
              src={franchise.logoUrl}
              size="lg"
              radius="sm"
              alt={franchise.name}
            >
              <IconBuilding size={24} />
            </Avatar>
            <div className="flex-1">
              <Group gap="xs" wrap="nowrap">
                {franchise.prefix && (
                  <Badge size="sm" variant="light" color="blue">
                    {franchise.prefix}
                  </Badge>
                )}
                <Text fw={600} size="lg">
                  {franchise.name}
                </Text>
              </Group>
              <Badge
                size="xs"
                variant="outline"
                color={franchise.role === "GM" ? "green" : "blue"}
                mt={4}
              >
                {franchise.role === "GM" ? "General Manager" : "Assistant GM"}
              </Badge>
            </div>
          </Paper>
        </div>

        {/* Quick Actions */}
        <div>
          <Title order={4} c={isDark ? "gray.3" : "gray.8"} mb="sm">
            Quick Actions
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {/* Create Transaction */}
            <Paper
              withBorder
              radius="md"
              p="lg"
              shadow="sm"
              onClick={() => navigate("/gm-hub/transactions/new")}
              role="button"
              tabIndex={0}
              aria-label="Create a new transaction"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate("/gm-hub/transactions/new");
                }
              }}
              className="cursor-pointer hover:shadow-md transition-shadow"
            >
              <Group>
                <ThemeIcon size="xl" radius="md" variant="light" color="green">
                  <IconUserPlus size={24} />
                </ThemeIcon>
                <div className="flex-1">
                  <Text fw={600}>Create Transaction</Text>
                  <Text size="xs" c="dimmed">
                    Submit a new roster move
                  </Text>
                </div>
              </Group>
            </Paper>

            {/* Pending Approvals */}
            <Paper
              withBorder
              radius="md"
              p="lg"
              shadow="sm"
              onClick={() => navigate("/gm-hub/pending")}
              role="button"
              tabIndex={0}
              aria-label={`View pending approvals, ${pendingCount} pending`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate("/gm-hub/pending");
                }
              }}
              className="cursor-pointer hover:shadow-md transition-shadow"
            >
              <Group>
                <ThemeIcon size="xl" radius="md" variant="light" color="orange">
                  <IconClipboardList size={24} />
                </ThemeIcon>
                <div className="flex-1">
                  <Group gap="xs" wrap="nowrap">
                    <Text fw={600}>Pending Approvals</Text>
                    {pendingCount > 0 && (
                      <Badge size="sm" color="orange" variant="filled">
                        {pendingCount}
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed">
                    Review awaiting your approval
                  </Text>
                </div>
              </Group>
            </Paper>

            {/* Transaction History */}
            <Paper
              withBorder
              radius="md"
              p="lg"
              shadow="sm"
              onClick={() => navigate("/gm-hub/history")}
              role="button"
              tabIndex={0}
              aria-label="View transaction history"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate("/gm-hub/history");
                }
              }}
              className="cursor-pointer hover:shadow-md transition-shadow"
            >
              <Group>
                <ThemeIcon size="xl" radius="md" variant="light" color="blue">
                  <IconHistory size={24} />
                </ThemeIcon>
                <div className="flex-1">
                  <Text fw={600}>Transaction History</Text>
                  <Text size="xs" c="dimmed">
                    View past transactions
                  </Text>
                </div>
              </Group>
            </Paper>
          </SimpleGrid>
        </div>
      </div>
    </Container>
  );
}

/**
 * GeneralManagerHub
 *
 * Hub for franchise GMs and AGMs to manage transactions and roster moves.
 * Access is controlled by whether the user is a GM or AGM of any franchise,
 * not by specific action permissions.
 */
export default function GeneralManagerHub() {
  const { franchise, loading, error } = useUserFranchise();

  // Show loader while checking GM/AGM status
  if (loading) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  // Show error if query failed
  if (error) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to verify GM/AGM status. {error.message}
        </Alert>
      </Container>
    );
  }

  // Only allow access if user is a GM or AGM of a franchise
  if (!franchise) {
    return (
      <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
        <Alert icon={<IconAlertCircle size={16} />} title="Access Denied" color="yellow">
          The GM Hub is only available to franchise General Managers and Assistant GMs.
        </Alert>
      </Container>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<GMHubDashboard />} />
      <Route path="/pending" element={<FranchisePendingApprovals />} />
      <Route path="/transactions/new" element={<TransactionCreate />} />
      <Route path="/history" element={<GMTransactionHistory />} />
    </Routes>
  );
}

/**
 * Placeholder for GM transaction history - reuses the shared TransactionHistory
 * but filtered to the user's franchise
 */
function GMTransactionHistory() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  return (
    <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/gm-hub")}
          size="sm"
        >
          Back to GM Hub
        </Button>
      </Group>
      <Title c={isDark ? "white" : "black"} size="h3" mb="md">
        Transaction History
      </Title>
      <Text c="dimmed">
        Your franchise's transaction history will be displayed here.
        This feature is coming soon.
      </Text>
    </Container>
  );
}
